import { useLocation, useParams } from 'react-router-dom';
import { compareBusinessDates, businessDate, systemClock } from '@sakhtar/calendar';
import { ModuleLayout, type ModuleTab } from './ModuleLayout.js';
import { computeActiveTab } from './tabMatch.js';
import { useVisibleTabs } from '../useNavAccess.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { useCapabilities } from '../../lib/capabilities.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { BookSwitcher } from '../../routes/finance/BookSwitcher.js';
import { useFinanceBook } from '../../routes/finance/useFinanceBook.js';
import type { FiscalPeriod, FiscalPeriodListResponse } from '../../lib/types/finance.js';

/**
 * Tab order follows the v2 Finance frame (`/t/kimia/finance`): overview,
 * journals, chart of accounts, fiscal periods, receivables, payables,
 * treasury, VAT, Moadian. The tabs below the reference set
 * (dimensions / year-end / reports) are real routes the frame does not draw;
 * they follow the reference tabs instead of being dropped. `hrefSuffix`
 * values are the routed paths and never change with tab order.
 */
export const FINANCE_TABS: readonly ModuleTab[] = [
  { id: 'overview', hrefSuffix: '', label: 'نمای کلی' },
  { id: 'journals', hrefSuffix: '/journals', label: 'اسناد حسابداری' },
  { id: 'accounts', hrefSuffix: '/accounts', label: 'کدینگ حساب‌ها' },
  { id: 'periods', hrefSuffix: '/fiscal-periods', label: 'دوره‌های مالی' },
  { id: 'ar', hrefSuffix: '/ar', label: 'دریافتنی‌ها' },
  { id: 'ap', hrefSuffix: '/ap', label: 'پرداختنی‌ها' },
  { id: 'treasury', hrefSuffix: '/treasury', label: 'خزانه' },
  { id: 'cheques', hrefSuffix: '/cheques', label: 'چک و صیاد' },
  { id: 'bank-recon', hrefSuffix: '/bank-recon', label: 'مغایرت بانکی' },
  { id: 'sales-accounts', hrefSuffix: '/sales-accounts', label: 'حساب‌های فروش و پورسانت' },
  { id: 'vat', hrefSuffix: '/vat', label: 'ارزش افزوده' },
  { id: 'modian', hrefSuffix: '/modian', label: 'سامانه مودیان' },
  { id: 'dimensions', hrefSuffix: '/dimensions', label: 'مراکز هزینه و پروژه‌ها' },
  { id: 'year-end', hrefSuffix: '/year-end', label: 'بستن سال' },
  { id: 'reports', hrefSuffix: '/reports', label: 'گزارش‌ها' },
  { id: 'books', hrefSuffix: '/books', label: 'دفترها' },
  { id: 'assistant', hrefSuffix: '/assistant', label: 'دستیار' },
];

/**
 * Tabs that are meaningful per book (D-022): the book switcher is offered on
 * them. Receivables, payables, treasury, cheques and the rest are not
 * duplicated per book (their effects fan out through posting), so no switcher.
 */
const BOOK_AWARE_TABS: ReadonlySet<string> = new Set([
  'overview',
  'journals',
  'accounts',
  'periods',
  'year-end',
  'reports',
  'vat',
  'modian',
]);

/** Taxpayer-system (Moadian) and VAT filing belong to the statutory book only (D-022 DEC-4). */
const STATUTORY_ONLY_TABS: ReadonlySet<string> = new Set(['vat', 'modian']);

const DEFAULT_SUBTITLE = 'خلاصه دفتر کل، اقلام باز و اسناد اخیر';

/** The fiscal period whose [startDate, endDate] range contains `today` (calendar-package comparison, no raw Date math). */
function currentFiscalPeriod(
  periods: readonly FiscalPeriod[],
  today: ReturnType<typeof systemClock.today>,
): FiscalPeriod | null {
  for (const period of periods) {
    try {
      if (
        compareBusinessDates(businessDate(period.startDate.slice(0, 10)), today) <= 0 &&
        compareBusinessDates(today, businessDate(period.endDate.slice(0, 10))) <= 0
      ) {
        return period;
      }
    } catch {
      // A malformed date on one period never hides the header.
    }
  }
  return null;
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — frame `/t/kimia/finance`, module
 *   header (mark + h1 «مالی و حسابداری» + «دورهٔ مالی جاری: … · باز/بسته»)
 *   and the tab strip
 * canonical pattern: ModuleLayout (module mark, title, subtitle line, primary
 *   CTA «سند حسابداری جدید», V2Tabs)
 * status: PARTIAL_REAL — the subtitle is fed from the real
 *   `GET .../accounting/fiscal-periods` (same endpoint JournalsPage /
 *   FiscalPeriodsPage use): the period containing today's business date, with
 *   its `postingState` (open/closed). While loading, on any error (403
 *   included) or when no period contains today, the previous static subtitle
 *   is kept — never an invented period. The frame's ghost «⋯ اقدام‌های دیگر»
 *   icon button has no action behind it and is not rendered. The per-tab KPI
 *   strip in `finExtra` is page-level data (each tab owns its figures) and is
 *   not drawn by this layout.
 */
export function FinanceModuleLayout() {
  const permittedTabs = useVisibleTabs('finance', FINANCE_TABS);
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const periods = useApiGet<FiscalPeriodListResponse>(
    slug ? `${base}/api/v1/accounting/fiscal-periods` : null,
    [slug],
  );
  const { hasCapability } = useCapabilities();
  // Presentation only — the API enforces the key.
  const book = useFinanceBook();
  const location = useLocation();
  const accessibleTabs = hasCapability('sales.posting-account.read')
    ? permittedTabs
    : permittedTabs.filter((tab) => tab.id !== 'sales-accounts');
  // The management book has no Moadian / VAT filing: hide those tabs while it is chosen.
  const tabs = book.statutoryFeaturesAvailable
    ? accessibleTabs
    : accessibleTabs.filter((tab) => !STATUTORY_ONLY_TABS.has(tab.id));
  const activeTabId =
    tabs.length > 0 && base ? computeActiveTab(location.pathname, `${base}/finance`, tabs).id : '';
  const showBookBar = BOOK_AWARE_TABS.has(activeTabId) || book.isAll;
  const current =
    periods.status === 'ready'
      ? currentFiscalPeriod(periods.data.items, systemClock.today())
      : null;
  const subtitle = current
    ? `دورهٔ مالی جاری: ${current.name} · ${current.postingState === 'open' ? 'باز' : 'بسته'}`
    : DEFAULT_SUBTITLE;

  return (
    <ModuleLayout
      moduleId="finance"
      title="مالی و حسابداری"
      subtitle={subtitle}
      tabs={tabs}
      toolbar={
        showBookBar && book.books.length > 1 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <BookSwitcher allowAll={activeTabId === 'journals'} />
            {!book.statutoryFeaturesAvailable ? (
              <span style={{ fontSize: 'var(--sk-fs-xs)', color: 'var(--sk-fg-muted)' }}>
                دفتر مدیریتی: سامانه مودیان و اظهارنامهٔ ارزش افزوده فقط برای دفتر قانونی فعال است.
              </span>
            ) : null}
          </div>
        ) : null
      }
      cta={{ label: 'سند حسابداری جدید', icon: 'plus', hrefSuffix: '/journals' }}
    />
  );
}
